import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";

import Modal from "../utils/Modal/Modal";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { createRequestWithdrawal } from "../../store/slices/payment/asyncThunk";
import {
  selectCreateRequestWithdrawalPending,
} from "../../store/slices/payment/slice";

import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";

import {
  ModalContainer,
  ModalContent,
  Row,
  TitleColumn,
  ModalTitle,
  ModalSubtitle,
  CloseButton,
  AvailableCard,
  AvailableLabel,
  AvailableValue,
  InputContainer,
  Label,
  Input,
  List,
  ListItem,
  ButtonGroup,
  CancelButton,
  ConfirmButton,
} from "./RequestWithdrawalModal.styles";

const MIN_WITHDRAWAL_USD = 100;

const RequestWithdrawalModal = ({
  isOpen,
  onClose,
  availableUsd = 0,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectCreateRequestWithdrawalPending);
  useLockBodyScroll(isOpen);

  const [walletAddress, setWalletAddress] = useState("");
  const [amount, setAmount] = useState("");

  if (!isOpen) return null;

  const numericAmount = Number(amount);
  const trimmedAddress = walletAddress.trim();
  const isInvalid =
    !trimmedAddress ||
    !numericAmount ||
    numericAmount < MIN_WITHDRAWAL_USD ||
    numericAmount > availableUsd;

  const handleClose = () => {
    if (isLoading) return;
    setWalletAddress("");
    setAmount("");
    onClose();
  };

  const handleSubmit = () => {
    if (isInvalid || isLoading) return;
    dispatch(
      createRequestWithdrawal({
        amount: numericAmount,
        walletAddress: trimmedAddress,
      }),
    )
      .unwrap()
      .then(() => {
        toast.success(
          "Withdrawal request submitted. Track its status in History below.",
        );
        setWalletAddress("");
        setAmount("");
        onSuccess?.();
        onClose();
      })
      .catch((err) => {
        toast.error(
          err?.message ?? "Error occurred while creating withdrawal request.",
        );
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      customOverlayStyles={{ zIndex: 3100 }}
    >
      <ModalContainer>
        <ModalContent>
          <Row>
            <TitleColumn>
              <ModalTitle>Withdraw</ModalTitle>
              <ModalSubtitle>
                Cash out your earnings as USDT on Solana.
              </ModalSubtitle>
            </TitleColumn>
            <CloseButton onClick={handleClose}>
              <CloseSvg width={20} height={20} />
            </CloseButton>
          </Row>

          <AvailableCard>
            <AvailableLabel>Available to withdraw</AvailableLabel>
            <AvailableValue>${availableUsd.toFixed(2)}</AvailableValue>
          </AvailableCard>

          <InputContainer>
            <Label>Solana wallet address (USDT)</Label>
            <Input
              placeholder="Your USDT address"
              value={walletAddress}
              onChange={(e) => setWalletAddress(e.target.value)}
              disabled={isLoading}
              autoComplete="off"
              spellCheck={false}
            />
          </InputContainer>

          <InputContainer>
            <Label>Amount (USD)</Label>
            <Input
              type="number"
              min={MIN_WITHDRAWAL_USD}
              max={availableUsd}
              placeholder={`$${MIN_WITHDRAWAL_USD}`}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={isLoading}
            />
          </InputContainer>

          <List>
            <ListItem>USDT on Solana network only.</ListItem>
            <ListItem>Minimum per withdrawal: ${MIN_WITHDRAWAL_USD}.</ListItem>
            <ListItem>Payouts run on Mondays and Tuesdays each week.</ListItem>
          </List>

          <ButtonGroup>
            <CancelButton onClick={handleClose} disabled={isLoading}>
              Cancel
            </CancelButton>
            <ConfirmButton
              onClick={handleSubmit}
              disabled={isInvalid || isLoading}
            >
              {isLoading ? (
                <l-bouncy size="18" speed="0.9" color="#ffffff" />
              ) : (
                "Request Withdrawal"
              )}
            </ConfirmButton>
          </ButtonGroup>
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
};

export default RequestWithdrawalModal;
